import type {
  CreateRatingMutation,
  CreateRatingMutationVariables,
} from '@/api/generated/graphql';
import { CREATE_RATING_MUTATION } from '@/api/queries/rating';
import { useAuthedClient } from '@/api/useAuthedClient';
import type { BrewType } from '@/lib/brewTypes';

import { useGraphQLMutation } from './useGraphQLMutation';

// Client-intent narrowing of the generated CreateRatingInput: callers must
// decide tastingNotes/brewType explicitly (nullable, not omittable), and
// BrewType comes from the lib allow-list so labels stay attached. The
// request below is typed with the generated variables, so any drift from
// the schema input still fails typecheck.
export interface CreateRatingInput {
  coffeeId: string;
  tastingNotes: string | null;
  brewType: BrewType | null;
  score: number;
}

interface UseCreateRatingReturn {
  createRating: (input: CreateRatingInput) => Promise<{ id: string } | null>;
  isSubmitting: boolean;
  error: Error | null;
}

export function useCreateRating(): UseCreateRatingReturn {
  const authedClient = useAuthedClient();
  const { mutate, isSubmitting, error } = useGraphQLMutation<
    CreateRatingInput,
    { id: string }
  >({
    mutationFn: async (input, signal) => {
      const response = await authedClient.request<
        CreateRatingMutation,
        CreateRatingMutationVariables
      >({
        document: CREATE_RATING_MUTATION,
        variables: { input },
        signal,
      });
      return response.createRating;
    },
  });

  return { createRating: mutate, isSubmitting, error };
}
